import { observer } from "mobx-react-lite";
import React, { useEffect, useCallback, useState, useRef, useMemo, } from "react";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useFormContext } from "./Form";
import { withFormWrapper, DEFAULT_ID } from "./withFormWrapper";
import { toJS } from "mobx";
import debounce from "debounce-promise";
import { bootstrapStyle } from "./Select";
import { StaticTypeahead } from "./StaticInput";
import { useTranslation } from "./hooks/useTranslation";
import ReactSelect, { components as defaultComponents, } from "react-select";
const DEBOUNCE_TIME = 500;
const defaultNewOptionValidator = (inputValue, selectValue, selectOptions) => {
    if (!inputValue)
        return false;
    const lowerCaseInputValue = inputValue.toLowerCase();
    const currentSelection = selectValue.map((value) => value.name.toLowerCase());
    const selectableOptions = selectOptions.map((option) => option.name.toLowerCase());
    return (!currentSelection.includes(lowerCaseInputValue) &&
        !selectableOptions.includes(lowerCaseInputValue));
};
const createValueContainer = (maxHeight) => {
    const ValueContainer = (props) => {
        return (React.createElement("div", { style: {
                flex: 1,
                alignItems: "center",
                display: "flex",
                width: "100%",
                flexWrap: "wrap",
                maxHeight: maxHeight,
                overflowY: "auto",
                padding: "1px 10px",
            } }, props.children));
    };
    return ValueContainer;
};
const createMultiValueContainer = (maxDisplayedSelectedValues, totalValues, onClickMoreValues) => {
    const MultiValueContainer = (props) => {
        const index = props.index ?? 0;
        if (index < maxDisplayedSelectedValues) {
            return React.createElement(defaultComponents.MultiValue, { ...props });
        }
        if (index === maxDisplayedSelectedValues) {
            const remainingCount = totalValues - maxDisplayedSelectedValues;
            return (React.createElement("div", { className: "select__multi-value", style: {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "2px",
                    display: "flex",
                    margin: "2px",
                    minWidth: "0",
                    boxSizing: "border-box",
                    cursor: onClickMoreValues ? "pointer" : "default",
                }, onMouseDown: (e) => e.preventDefault(), onClick: onClickMoreValues },
                React.createElement("div", { className: "select__multi-value__label", role: "button", style: {
                        borderRadius: "2px",
                        color: "#333",
                        fontSize: "85%",
                        overflow: "hidden",
                        padding: "3px",
                        paddingLeft: "6px",
                        paddingRight: "6px",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        boxSizing: "border-box",
                    } },
                    "+",
                    remainingCount,
                    " More")));
        }
        return null;
    };
    return MultiValueContainer;
};
const RawTypeahead = observer(({ name, onChange, multiple = false, loadOptions, pagedLoadOptions, disabled = false, formatter = (option) => option.label, autoFocus = false, placeholder, uniqueId = DEFAULT_ID, staticFormatter = (option) => option.label, label, allowNew = false, newOptionLabelFormatter, newOptionFormatter, newOptionValidator = allowNew ? defaultNewOptionValidator : undefined, noOptionsMessage, isClearable = true, dataId, dataLabel, dataTestId, forceAlwaysLive = false, components, portal, maxDisplayedSelectedValues, onClickMoreValues, maxHeight, }) => {
    const { values, formDisabled, isStatic } = useFormContext();
    const mounted = useRef(false);
    const [options, setOptions] = useState([]);
    const emptyValue = multiple ? [] : null;
    const t = useTranslation();
    if (placeholder === undefined) {
        placeholder = `${t("Select")}...`;
    }
    const currentValue = toJS(values.get(name)) || [];
    const valueLength = Array.isArray(currentValue) ? currentValue.length : 0;
    const customComponents = useMemo(() => {
        if (!maxDisplayedSelectedValues || !multiple) {
            return components;
        }
        return {
            ...components,
            ValueContainer: components?.ValueContainer ||
                (maxHeight
                    ? createValueContainer(maxHeight)
                    : defaultComponents?.ValueContainer),
            MultiValue: components?.MultiValue ||
                createMultiValueContainer(maxDisplayedSelectedValues, valueLength, onClickMoreValues),
        };
    }, [
        maxDisplayedSelectedValues,
        multiple,
        components,
        valueLength,
        onClickMoreValues,
        maxHeight,
    ]);
    const handleLoadOptions = useCallback((inputValue) => {
        if (!loadOptions || !mounted.current) {
            return Promise.resolve([]);
        }
        return Promise.resolve()
            .then(() => loadOptions(inputValue))
            .then((options) => {
            if (mounted.current) {
                setOptions(options);
            }
            return options;
        }, (error) => {
            // Unmounting mid-load aborts the picker's query. Report other
            // failures, which react-select would otherwise swallow.
            if (error?.name !== "AbortError")
                reportError(error);
            return [];
        });
    }, [loadOptions, setOptions]);
    // Track mounted state across the component lifetime only, not across
    // effect re-runs caused by dependency changes.
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const paged = !!pagedLoadOptions;
    const [pagedState, setPagedState] = useState({
        options: [],
        totalRecords: 0,
    });
    const [pagedLoading, setPagedLoading] = useState(false);
    const [pagedInput, setPagedInput] = useState("");
    // Monotonic request id: a page landing after a newer request started
    // (fast scroll + retype races) must not clobber the newer result.
    const pagedRequestRef = useRef(0);
    useEffect(() => {
        if (!loadOptions && !pagedLoadOptions) {
            console.error("Typeahead: either loadOptions or pagedLoadOptions must be " +
                "provided — the picker will render empty without one");
        }
        if (allowNew && pagedLoadOptions) {
            console.error("Typeahead: allowNew is not supported with pagedLoadOptions and " +
                "will be ignored");
        }
    }, [loadOptions, pagedLoadOptions, allowNew]);
    const runPagedLoad = useCallback((inputValue, offset) => {
        if (!pagedLoadOptions) {
            return;
        }
        const requestId = ++pagedRequestRef.current;
        setPagedLoading(true);
        pagedLoadOptions(inputValue, offset)
            .then(({ options: page, totalRecords }) => {
            if (pagedRequestRef.current !== requestId || !mounted.current) {
                return;
            }
            setPagedState((previous) => ({
                options: offset === 0 ? page : [...previous.options, ...page],
                totalRecords,
            }));
            setPagedLoading(false);
        })
            .catch((error) => {
            if (pagedRequestRef.current !== requestId || !mounted.current) {
                return;
            }
            console.error("Typeahead: pagedLoadOptions rejected", error);
            setPagedLoading(false);
        });
    }, [pagedLoadOptions]);
    // Dispatch through the latest runPagedLoad for the same reason the
    // unpaged path does: a pending timer created before the parent handed
    // down a fresh pagedLoadOptions must not query through the stale one.
    const runPagedLoadRef = useRef(runPagedLoad);
    useEffect(() => {
        runPagedLoadRef.current = runPagedLoad;
    }, [runPagedLoad]);
    const debouncedPagedLoad = useMemo(() => debounce((inputValue) => runPagedLoadRef.current(inputValue, 0), DEBOUNCE_TIME), []);
    const handlePagedInputChange = useCallback((inputValue, meta) => {
        if (meta.action === "input-change") {
            setPagedInput(inputValue);
            debouncedPagedLoad(inputValue);
        }
        else if (meta.action === "menu-close" && pagedInput !== "") {
            setPagedInput("");
            debouncedPagedLoad("");
        }
    }, [debouncedPagedLoad, pagedInput]);
    const handleMenuScrollToBottom = useCallback(() => {
        if (pagedLoading ||
            pagedState.options.length >= pagedState.totalRecords) {
            return;
        }
        runPagedLoad(pagedInput, pagedState.options.length);
    }, [pagedLoading, pagedState, pagedInput, runPagedLoad]);
    const PagedMenuList = useMemo(() => {
        const loadedCount = pagedState.options.length;
        const { totalRecords } = pagedState;
        const MenuList = (props) => (React.createElement(defaultComponents.MenuList, { ...props },
            props.children,
            totalRecords > loadedCount && (React.createElement("div", { "data-testid": "typeahead-paged-footer", style: {
                    padding: "6px 12px",
                    color: "#666",
                    fontSize: "85%",
                    textAlign: "center",
                } }, `${t("Showing")} ${loadedCount} ${t("of")} ${totalRecords} — ${t("Scroll to load more")}`))));
        return MenuList;
    }, [pagedState, t]);
    // Debounced reloads (typing, menu close, blur) fire up to DEBOUNCE_TIME
    // after they were triggered. By then the parent may have rendered a new
    // loadOptions closure carrying fresh data, so dispatch through the latest
    // one: a pending timer must not overwrite newer options with the stale
    // results its original closure would produce.
    const handleLoadOptionsRef = useRef(handleLoadOptions);
    useEffect(() => {
        handleLoadOptionsRef.current = handleLoadOptions;
    }, [handleLoadOptions]);
    const debouncedLoadOptions = useMemo(() => debounce((inputValue) => handleLoadOptionsRef.current(inputValue), DEBOUNCE_TIME), []);
    const handleChange = React.useCallback((selected) => {
        if (selected === null) {
            if (paged) {
                runPagedLoad("", 0);
            }
            else {
                handleLoadOptions("");
            }
        }
        const changedValue = selected
            ? multiple
                ? selected.flat()
                : selected
            : emptyValue;
        if (onChange) {
            onChange(changedValue, values.get(name));
        }
        values.set(name, changedValue);
    }, [
        onChange,
        values,
        emptyValue,
        name,
        handleLoadOptions,
        paged,
        runPagedLoad,
    ]);
    useEffect(() => {
        if (paged) {
            runPagedLoad("", 0);
        }
        else {
            handleLoadOptions("");
        }
    }, [paged, runPagedLoad, handleLoadOptions]);
    if (isStatic && !forceAlwaysLive) {
        return (React.createElement(StaticTypeahead, { name: name, label: label, staticFormatter: staticFormatter, multiple: multiple, dataId: dataId, dataLabel: dataLabel, dataTestId: dataTestId }));
    }
    if (paged) {
        return (React.createElement(ReactSelect, { "aria-labelledby": `form-group-${name}-${uniqueId}`, id: `form-group-${name}-${uniqueId}`, styles: bootstrapStyle, components: { ...customComponents, MenuList: PagedMenuList }, isMulti: multiple, value: toJS(values.get(name)), onChange: handleChange, isClearable: isClearable, options: pagedState.options, inputValue: pagedInput, onInputChange: handlePagedInputChange, onMenuScrollToBottom: handleMenuScrollToBottom, 
            // react-select only fires onMenuScrollToBottom when it captures
            // menu scroll, and its default disables capture on touch-capable
            // devices — which would silently disable load-more there.
            captureMenuScroll: true, isLoading: pagedLoading, 
            // The server already filtered the page for the current input;
            // react-select's client-side filter would hide rows whose
            // formatted label doesn't contain the search term.
            filterOption: () => true, getOptionValue: JSON.stringify, getOptionLabel: formatter, noOptionsMessage: () => noOptionsMessage || `${t("Type to Search")}...`, className: "react-select ag-custom-component-popup", isDisabled: disabled || formDisabled, autoFocus: autoFocus, menuPortalTarget: portal, classNamePrefix: "select", placeholder: placeholder, "data-id": dataId, "data-testid": dataTestId }));
    }
    const SelectComponent = allowNew ? AsyncCreatableSelect : AsyncSelect;
    const creatableSelectProps = {
        getNewOptionData: newOptionFormatter,
        formatCreateLabel: newOptionLabelFormatter,
        isValidNewOption: newOptionValidator,
        createOptionPosition: "first",
    };
    return (React.createElement(SelectComponent, { "aria-labelledby": `form-group-${name}-${uniqueId}`, id: `form-group-${name}-${uniqueId}`, styles: bootstrapStyle, components: customComponents, isMulti: multiple, value: toJS(values.get(name)), onChange: handleChange, isClearable: isClearable, defaultOptions: options, onInputChange: debouncedLoadOptions, loadOptions: debouncedLoadOptions, getOptionValue: JSON.stringify, getOptionLabel: formatter, noOptionsMessage: () => noOptionsMessage || `${t("Type to Search")}...`, className: "react-select ag-custom-component-popup", isDisabled: disabled || formDisabled, autoFocus: autoFocus, menuPortalTarget: portal, classNamePrefix: "select", placeholder: placeholder, ...(allowNew ? creatableSelectProps : undefined), "data-id": dataId, "data-testid": dataTestId }));
});
export const Typeahead = withFormWrapper({ WrappedComponent: RawTypeahead });
